import React, { useEffect, useState } from 'react'

import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'
import { Button, Alert } from '@repo/ui'
import { ArrowLeftIcon, KeyRoundIcon, MessageSquareIcon, Trash2Icon } from 'lucide-react'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { FieldLabel } from 'components/UI/FieldLabel'
import { SectionLabel } from 'components/UI/SectionLabel'
import { Input } from 'components/UI/Input'
import { PhoneField } from 'components/Fields/PhoneField'
import { useSnackbar } from 'components/Snackbar'
import { CodeExpiryNotice } from 'components/auth'
import { OTP_CODE_TTL_SECONDS, TWO_FACTOR_TICKET_TTL_SECONDS } from 'hooks/useCodeExpiry'

import {
  useAuthFactorsQuery,
  useConfirmSmsEnrollmentMutation,
  useConfirmTotpEnrollmentMutation,
  useRegenerateRecoveryCodesMutation,
  useRemoveAuthFactorMutation,
  useStartSmsEnrollmentMutation,
  useStartTotpEnrollmentMutation,
  useStartStepUpMutation,
  useVerifyStepUpMutation,
  type AuthFactor,
  type StepUpChallenge,
  type StartTotpEnrollmentResponse,
} from 'hooks/api/useAuthFactors'

import { RecoveryCodesStep } from './RecoveryCodesStep'

type Step = 'list' | 'stepUp' | 'totpEnroll' | 'smsPhone' | 'smsCode' | 'recoveryCodes' | 'removeConfirm' | 'regenerateConfirm'

/** What runs once a step-up passes. */
type PendingAction = { kind: 'totp' } | { kind: 'sms' } | { kind: 'remove'; factor: AuthFactor } | { kind: 'regenerate' }

const TOTP_CODE_LENGTH = 6
// VRG-490: 6, matching StartSmsEnrollmentUseCase and the login challenge.
// Was 4 while enrollment fell through to OtpEntity's Math.random() default.
const SMS_CODE_LENGTH = 6

interface TwoFactorDrawerProps {
  open: boolean
  onClose: () => void
}

/**
 * VRG-490 (D8) — the whole two-factor surface for `apps/app`: enrolled
 * methods, add authenticator app / add phone, remove, recovery codes.
 *
 * ONE drawer, a step machine (working-conventions §52) — never a second or
 * third sheet stacked on top. Every step swaps this drawer's body and
 * (except the terminal `recoveryCodes` step) gets a back arrow in
 * `headerLeft`, the same pattern `ScanTag.tsx` already established for
 * exactly this situation.
 */
export const TwoFactorDrawer: React.FC<TwoFactorDrawerProps> = ({ open, onClose }) => {
  const theme = useTheme()
  const { openSnackbar } = useSnackbar()

  const [step, setStep] = useState<Step>('list')
  const [totpEnrollment, setTotpEnrollment] = useState<StartTotpEnrollmentResponse | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [phone, setPhone] = useState('')
  const [smsReference, setSmsReference] = useState<string | null>(null)
  const [smsCode, setSmsCode] = useState('')
  /*
   * VRG-720 — deadlines for this drawer's two code steps. Different clocks,
   * because they are different mechanisms:
   *
   *  - the SMS ENROLMENT code is 10 minutes (`SMS_CODE_TTL_SECONDS`, which
   *    `SmsSecondFactorCodeService` writes onto the row's `expires_at` and
   *    checks there rather than leaning on a store TTL);
   *  - the STEP-UP ticket is 5 minutes (`CHALLENGE_TTL_SECONDS`) — the same
   *    `TwoFactorChallengeService` as the login challenge, issued here with
   *    `purpose: 'step_up'`.
   */
  const [smsCodeIssuedAt, setSmsCodeIssuedAt] = useState<number | null>(null)
  const [stepUpIssuedAt, setStepUpIssuedAt] = useState<number | null>(null)
  const [removeTarget, setRemoveTarget] = useState<AuthFactor | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Step-up (security review 2026-10-09): every add / remove / regenerate while a factor
  // exists first re-proves an existing factor. `pending` is what continues afterwards.
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [stepUp, setStepUp] = useState<StepUpChallenge | null>(null)
  const [stepUpCode, setStepUpCode] = useState('')

  const { factors, isLoading, refetch } = useAuthFactorsQuery()
  const { startTotpEnrollmentAsync, isStartingTotpEnrollment } = useStartTotpEnrollmentMutation()
  const { confirmTotpEnrollmentAsync, isConfirmingTotpEnrollment } = useConfirmTotpEnrollmentMutation()
  const { startSmsEnrollmentAsync, isStartingSmsEnrollment } = useStartSmsEnrollmentMutation()
  const { confirmSmsEnrollmentAsync, isConfirmingSmsEnrollment } = useConfirmSmsEnrollmentMutation()
  const { removeAuthFactorAsync, isRemovingAuthFactor } = useRemoveAuthFactorMutation()
  const { regenerateRecoveryCodesAsync, isRegeneratingRecoveryCodes } = useRegenerateRecoveryCodesMutation()
  const { startStepUpAsync, isStartingStepUp } = useStartStepUpMutation()
  const { verifyStepUpAsync, isVerifyingStepUp } = useVerifyStepUpMutation()

  // Reset ONLY on the closed → open transition (same reasoning as
  // PhoneVerificationDrawer: a stale step from a previous open must not
  // reappear, and re-running this on every profile refetch would throw the
  // user back to `list` mid-flow).
  useEffect(() => {
    if (open) {
      setStep('list')
      setTotpEnrollment(null)
      setTotpCode('')
      setPhone('')
      setSmsReference(null)
      setSmsCode('')
      setSmsCodeIssuedAt(null)
      setStepUpIssuedAt(null)
      setRemoveTarget(null)
      setRecoveryCodes(null)
      setError(null)
      setPending(null)
      setStepUp(null)
      setStepUpCode('')
    }
  }, [open])

  const hasTotp = factors.some(f => f.type === 'totp' && f.verified)
  const hasSms = factors.some(f => f.type === 'sms' && f.verified)
  const hasVerifiedFactor = factors.some(f => f.verified)

  const runPending = (action: PendingAction) => {
    setError(null)
    if (action.kind === 'totp') handleStartTotp()
    if (action.kind === 'sms') { setPhone(''); setStep('smsPhone') }
    if (action.kind === 'remove') { setRemoveTarget(action.factor); setStep('removeConfirm') }
    if (action.kind === 'regenerate') setStep('regenerateConfirm')
  }

  /** Run `action` now, or first ask the owner to confirm with an existing factor. */
  const requireStepUp = async (action: PendingAction) => {
    if (!hasVerifiedFactor) {
      runPending(action)
      return
    }
    setError(null)
    try {
      const challenge = await startStepUpAsync()
      setStepUp(challenge)
      setStepUpCode('')
      setPending(action)
      setStepUpIssuedAt(Date.now())
      setStep('stepUp')
    } catch (err) {
      openSnackbar({ severity: 'error', title: err instanceof Error ? err.message : 'Could not start verification' })
    }
  }

  const handleVerifyStepUp = async () => {
    if (!stepUp || !pending || !stepUpCode.trim()) return
    setError(null)
    try {
      await verifyStepUpAsync({ ticket: stepUp.ticket, code: stepUpCode.trim() })
      const next = pending
      setPending(null)
      setStep('list')
      runPending(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code isn't right. Try again.")
    }
  }

  const handleStartTotp = async () => {
    setError(null)
    try {
      const response = await startTotpEnrollmentAsync()
      setTotpEnrollment(response)
      setTotpCode('')
      setStep('totpEnroll')
    } catch (err) {
      openSnackbar({ severity: 'error', title: err instanceof Error ? err.message : 'Could not start setup' })
    }
  }

  const handleConfirmTotp = async () => {
    if (totpCode.trim().length !== TOTP_CODE_LENGTH) return
    setError(null)
    try {
      const response = await confirmTotpEnrollmentAsync({ code: totpCode.trim() })
      refetch()
      if (response.recoveryCodes) {
        setRecoveryCodes(response.recoveryCodes)
        setStep('recoveryCodes')
      } else {
        setStep('list')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code isn't right. Try again.")
    }
  }

  const handleSendSmsCode = async () => {
    if (!phone.trim()) return
    setError(null)
    try {
      const response = await startSmsEnrollmentAsync({ phone: phone.trim() })
      setSmsReference(response.reference)
      setSmsCode('')
      setSmsCodeIssuedAt(Date.now())
      setStep('smsCode')
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't send a code to that number.")
    }
  }

  const handleConfirmSms = async () => {
    if (!smsReference || smsCode.trim().length !== SMS_CODE_LENGTH) return
    setError(null)
    try {
      const response = await confirmSmsEnrollmentAsync({ reference: smsReference, code: smsCode.trim() })
      refetch()
      if (response.recoveryCodes) {
        setRecoveryCodes(response.recoveryCodes)
        setStep('recoveryCodes')
      } else {
        setStep('list')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code isn't right. Try again.")
    }
  }

  const handleRemove = async () => {
    if (!removeTarget) return
    try {
      await removeAuthFactorAsync({ factorId: removeTarget.id })
      refetch()
      setStep('list')
      openSnackbar({ severity: 'success', title: 'Removed from your account.' })
    } catch (err) {
      openSnackbar({ severity: 'error', title: err instanceof Error ? err.message : 'Could not remove that method' })
    }
  }

  const handleRegenerateCodes = async () => {
    try {
      const response = await regenerateRecoveryCodesAsync()
      setRecoveryCodes(response.recoveryCodes)
      setStep('recoveryCodes')
    } catch (err) {
      openSnackbar({ severity: 'error', title: err instanceof Error ? err.message : 'Could not generate codes' })
    }
  }

  // §43 — regenerating stops every EXISTING code from working the instant it
  // confirms (including a printed/saved copy), so it's gated behind a step
  // that states that consequence before it happens — not a bare "Generate
  // codes" action that reads as "show me my codes". Mirrors the platform's
  // `ConfirmSettingModal` gate on the same action; the app has no shared
  // confirm-modal primitive, so this is a step in the existing step machine
  // (§52), the same way `removeConfirm` above already gates Remove.

  const backArrow = (to: Step) => (
    <Box
      sx={{ position: 'relative', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.primary', flexShrink: 0 }}
    >
      <ArrowLeftIcon size={20} />
      <Box
        onClick={() => { setError(null); setStep(to) }}
        role="button"
        tabIndex={0}
        aria-label="Back"
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStep(to) } }}
        sx={{ position: 'absolute', inset: '-8px', cursor: 'pointer' }}
      />
    </Box>
  )

  const title =
    step === 'list' ? 'Two-factor authentication'
    : step === 'stepUp' ? "Confirm it's you"
    : step === 'totpEnroll' ? 'Add authenticator app'
    : step === 'smsPhone' ? 'Add phone number'
    : step === 'smsCode' ? 'Enter verification code'
    : step === 'recoveryCodes' ? 'Save your recovery codes'
    : step === 'regenerateConfirm' ? 'Regenerate recovery codes?'
    : 'Remove method'

  const headerLeft =
    step === 'list' || step === 'recoveryCodes' ? undefined
    : step === 'smsCode' ? backArrow('smsPhone')
    : step === 'stepUp' ? backArrow('list')
    : backArrow('list')

  return (
    <DrawerPopup
      title={title}
      handleOpen={open}
      handleClose={onClose}
      fullScreen={false}
      headerLeft={headerLeft}
      child={
        step === 'list' ? (
          <Stack spacing={4}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              Add a second step when you sign in, using an authenticator app or a text message.
            </Typography>

            {!isLoading && factors.length > 0 && (
              <Stack spacing={1}>
                <SectionLabel first tone="menu">Methods set up</SectionLabel>
                <Stack divider={<Divider />} spacing={0}>
                  {factors.map(factor => (
                    <Stack key={factor.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        {factor.type === 'totp' ? <KeyRoundIcon size={20} strokeWidth={1.5} /> : <MessageSquareIcon size={20} strokeWidth={1.5} />}
                        <Stack>
                          <Typography variant="body2">
                            {factor.label || (factor.type === 'totp' ? 'Authenticator app' : 'Text message')}
                          </Typography>
                          {factor.type === 'sms' && factor.phoneLastFour && (
                            <Typography variant="body3" sx={{ color: theme.palette.text.secondary }}>
                              Ending in {factor.phoneLastFour.slice(-2)}
                            </Typography>
                          )}
                          {/* The number replied STOP: codes cannot be texted until it texts START. */}
                          {factor.type === 'sms' && factor.optedOutAt && (
                            <Typography variant="body2" sx={{ fontSize: 12, color: theme.palette.warning.main }} data-testid="sms-opted-out-badge">
                              Texts are blocked (replied STOP)
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                      <Box
                        onClick={() => requireStepUp({ kind: 'remove', factor })}
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${factor.label || factor.type}`}
                        sx={{ color: theme.palette.text.secondary, cursor: 'pointer', display: 'flex' }}
                      >
                        <Trash2Icon size={18} strokeWidth={1} />
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}

            {/* §46 — already-enrolled is record state, so the Add row hides
                (the row above already carries the resolving Remove action)
                rather than disabling with no explanation.
                §29 — 12px between the two buttons themselves (spacing={1.5} on
                apps/app's 8px base), independent of the surrounding Stack's
                32px section rhythm (spacing={4}). */}
            {(!hasTotp || !hasSms) && (
              <Stack spacing={1.5}>
                {!hasTotp && (
                  <Button variant="secondary" fullWidth loading={isStartingTotpEnrollment} disabled={isStartingTotpEnrollment || isStartingStepUp} onClick={() => requireStepUp({ kind: 'totp' })}>
                    Add authenticator app
                  </Button>
                )}
                {!hasSms && (
                  <Button variant="secondary" fullWidth disabled={isStartingStepUp} onClick={() => requireStepUp({ kind: 'sms' })}>
                    Add phone
                  </Button>
                )}
              </Stack>
            )}

            {(hasTotp || hasSms) && (
              <>
                <Divider />
                <Stack spacing={2}>
                  <Typography variant="body2">Recovery codes</Typography>
                  <Typography variant="body3" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
                    Single-use codes to sign in if you lose access to your other methods. They're shown once and
                    can't be retrieved again, only replaced with a new set.
                  </Typography>
                  <Button variant="secondary" fullWidth disabled={isStartingStepUp} onClick={() => requireStepUp({ kind: 'regenerate' })}>
                    Regenerate recovery codes
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        ) : step === 'totpEnroll' ? (
          <Stack spacing={4}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              Scan this code with an authenticator app (1Password, Authy, Google Authenticator), or enter the key
              manually, then type the 6-digit code it shows.
            </Typography>
            {totpEnrollment && (
              <Stack alignItems="center" spacing={3}>
                {/* VRG-490 round 2 — square, no radius, recessed fill. Same
                    "one step darker than the surface" idea `@repo/ui`'s
                    `fieldFill`/`fieldBorderColor` give the platform's Security
                    page (§31), but stated with THIS app's own theme tokens
                    (`grey[50]` / `divider` — the same pair the pre-existing
                    manual-key block already used) rather than importing the
                    packages/ui helpers: those are typed against `@mui/material`'s
                    `Theme`, and this app resolves a structurally-identical but
                    nominally DIFFERENT `Theme` instance (duplicate `@mui/material`
                    installs in the monorepo — a pnpm-hoisting artifact, not a
                    real incompatibility), so passing this file's `theme` into
                    them fails `tsc` even though it would work at runtime. */}
                <Box sx={{ p: 3, borderRadius: 0, backgroundColor: theme.palette.grey[50], border: `1px solid ${theme.palette.divider}` }}>
                  <Box
                    component="img"
                    src={totpEnrollment.qrCodeDataUri}
                    alt="Scan this QR code with your authenticator app"
                    sx={{ width: 180, height: 180, display: 'block' }}
                  />
                </Box>
                <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Typography variant="body3" sx={{ color: theme.palette.text.secondary }}>
                    Can't scan? Enter this key manually
                  </Typography>
                  <Box sx={{ width: '100%', px: 3, py: 2, borderRadius: 0, backgroundColor: theme.palette.grey[50], border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                    <Typography variant="body3" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {totpEnrollment.secret}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            )}
            <Stack>
              <Input
                label="6-digit code"
                placeholder="000000"
                value={totpCode}
                onChange={(value: string) => { setTotpCode(value.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH)); setError(null) }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (!isConfirmingTotpEnrollment) handleConfirmTotp() } }}
                disabled={isConfirmingTotpEnrollment}
                error={Boolean(error)}
                autoFocus
                inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: TOTP_CODE_LENGTH }}
              />
              {error && (
                <Typography variant="body3" sx={{ color: theme.palette.error.main, mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Stack>
            <Button
              variant="primary"
              fullWidth
              loading={isConfirmingTotpEnrollment}
              disabled={isConfirmingTotpEnrollment || totpCode.trim().length !== TOTP_CODE_LENGTH}
              onClick={handleConfirmTotp}
            >
              Confirm
            </Button>
          </Stack>
        ) : step === 'smsPhone' ? (
          <Stack spacing={4}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              We'll text a verification code to this number every time you sign in.
            </Typography>
            <Stack>
              <FieldLabel label="Phone number" />
              <PhoneField value={phone} onChange={v => { setPhone(v); setError(null) }} error={Boolean(error)} />
            </Stack>
            {/*
              VRG-490 D4 — legally load-bearing, do not paraphrase away. This
              is the consent event for security-category SMS: from this point
              on, security texts (transfer confirmation codes, password-changed
              alerts) are sent to this number and cannot be turned off while
              text-message verification is active. Stated plainly, BEFORE the
              user sends a code, per the batch brief's explicit instruction.
              Round 2 — plain text, not a warning Alert (still immediately
              adjacent to Send code, so the consent sits with the action it
              consents to).
            */}
            <Typography variant="body3" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              Verifying this number for two-factor authentication also turns on security text
              messages for it. We&apos;ll always text you a code to confirm certificate transfers
              and to let you know if your password changes. These security texts can&apos;t be
              turned off while text-message verification is active.
            </Typography>
            {error && (
              <Typography variant="body3" sx={{ color: theme.palette.error.main }}>
                {error}
              </Typography>
            )}
            <Button variant="primary" fullWidth loading={isStartingSmsEnrollment} disabled={isStartingSmsEnrollment || !phone.trim()} onClick={handleSendSmsCode}>
              Send code
            </Button>
          </Stack>
        ) : step === 'smsCode' ? (
          <Stack spacing={4}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              Enter the {SMS_CODE_LENGTH}-digit code we sent to {phone}.
            </Typography>
            <Stack>
              <Input
                label="Verification code"
                placeholder="000000"
                value={smsCode}
                onChange={(value: string) => { setSmsCode(value.replace(/\D/g, '').slice(0, SMS_CODE_LENGTH)); setError(null) }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (!isConfirmingSmsEnrollment) handleConfirmSms() } }}
                disabled={isConfirmingSmsEnrollment}
                error={Boolean(error)}
                autoFocus
                inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: SMS_CODE_LENGTH }}
              />
              {error && (
                <Typography variant="body3" sx={{ color: theme.palette.error.main, mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Stack>
            <CodeExpiryNotice
              issuedAt={smsCodeIssuedAt}
              ttlSeconds={OTP_CODE_TTL_SECONDS}
              resendLabel='Use Resend code below'
            />
            <Stack spacing={1.5}>
              <Button
                variant="primary"
                fullWidth
                loading={isConfirmingSmsEnrollment}
                disabled={isConfirmingSmsEnrollment || smsCode.trim().length !== SMS_CODE_LENGTH}
                onClick={handleConfirmSms}
              >
                Verify
              </Button>
              <Button variant="secondary" fullWidth disabled={isStartingSmsEnrollment} onClick={handleSendSmsCode}>
                Resend code
              </Button>
            </Stack>
          </Stack>
        ) : step === 'stepUp' ? (
          <Stack spacing={4}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              {stepUp?.factorType === 'sms'
                ? `Before changing your verification methods, enter the code we texted to the phone ending in ${stepUp?.maskedPhone?.replace(/\D/g, '').slice(-4) ?? '…'}.`
                : 'Before changing your verification methods, enter the 6-digit code from your authenticator app. A recovery code also works.'}
            </Typography>
            <Stack>
              <Input
                label="Verification code"
                placeholder="000000"
                value={stepUpCode}
                onChange={(value: string) => { setStepUpCode(value.trim()); setError(null) }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (!isVerifyingStepUp) handleVerifyStepUp() } }}
                disabled={isVerifyingStepUp}
                error={Boolean(error)}
                autoFocus
                inputProps={{ autoComplete: 'one-time-code' }}
              />
              {error && (
                <Typography variant="body3" sx={{ color: theme.palette.error.main, mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Stack>
            <CodeExpiryNotice
              issuedAt={stepUpIssuedAt}
              ttlSeconds={TWO_FACTOR_TICKET_TTL_SECONDS}
              resendLabel='Go back and start again'
            />
            <Button
              variant="primary"
              fullWidth
              loading={isVerifyingStepUp}
              disabled={isVerifyingStepUp || !stepUpCode.trim()}
              onClick={handleVerifyStepUp}
            >
              Continue
            </Button>
          </Stack>
        ) : step === 'recoveryCodes' ? (
          <RecoveryCodesStep codes={recoveryCodes ?? []} onDone={() => setStep('list')} />
        ) : step === 'regenerateConfirm' ? (
          <Stack spacing={4}>
            <Alert
              severity="warning"
              description="Your current recovery codes will stop working immediately, including any copy you've already printed or saved. You'll be shown a new set of codes once, and only once — save them somewhere safe before leaving that screen."
            />
            <Stack spacing={1.5}>
              <Button variant="primary" fullWidth loading={isRegeneratingRecoveryCodes} onClick={handleRegenerateCodes}>
                Regenerate recovery codes
              </Button>
              <Button variant="secondary" fullWidth disabled={isRegeneratingRecoveryCodes} onClick={() => setStep('list')}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={4}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              {`Are you sure you want to remove "${removeTarget?.label || (removeTarget?.type === 'totp' ? 'authenticator app' : 'text message')}" as a two-factor method? You'll no longer be able to use it to sign in.`}
            </Typography>
            <Stack spacing={1.5}>
              <Button variant="primary" destructive fullWidth loading={isRemovingAuthFactor} onClick={handleRemove}>
                Remove
              </Button>
              <Button variant="secondary" fullWidth disabled={isRemovingAuthFactor} onClick={() => setStep('list')}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        )
      }
    />
  )
}
